import type { ActionContext } from 'vuex';

import { NotificationTypes } from '@/components/app-kit/notifications-promotions/constants/notifications';
import { INotificationMessageBySlot, INotificationMessagesBySlotPagination, INotificationPromoCode } from '@/store/types/INotificationsMessagesBySlot';

type TNotificationsType = Exclude<NotificationTypes, NotificationTypes.PROMOCODE>;

type TNotifications = Record<TNotificationsType, {
  messages: INotificationMessageBySlot[],
  pagination: INotificationMessagesBySlotPagination
}>;
interface IState {
  notifications: TNotifications,
  promoCodes: INotificationPromoCode[],
}

const moduleState: IState = {
  notifications: {
    [NotificationTypes.PROMOTION]: {
      messages: [],
      pagination: {
        page: null,
        pageSize: null,
        pageCount: null,
      },
    },
    [NotificationTypes.ORDER]: {
      messages: [],
      pagination: {
        page: null,
        pageSize: null,
        pageCount: null,
      },
    },
    [NotificationTypes.MESSAGE]: {
      messages: [],
      pagination: {
        page: null,
        pageSize: null,
        pageCount: null,
      },
    },
    [NotificationTypes.BONUS]: {
      messages: [],
      pagination: {
        page: null,
        pageSize: null,
        pageCount: null,
      },
    },
  },
  promoCodes: [
  ],
};

export default {
  namespaced: true,

  state: () => moduleState,

  actions: {
    async fetchNotificationsBySlot({ commit }: ActionContext<IState, null>, slot: TNotificationsType) {
      try {
        const page = 1;
        const { messages, pagination } = await this.$apiBFF.fetchSlotNotification(slot, page);
        commit('setMessages', { messages, pagination, slot });
      } catch (e) {
        this.$logger.error(e);
      }
    },
    async fetchMoreNotificationsBySlot({ state, commit }: ActionContext<IState, null>, slot: TNotificationsType) {
      try {
        const page = state.notifications[slot]?.pagination?.page ?? 1;
        const { messages, pagination } = await this.$apiBFF.fetchSlotNotification(slot, page + 1);
        if (messages.length) {
          commit('addMessages', { messages, pagination, slot });
        }
      } catch (e) {
        this.$logger.error(e);
      }
    },
    async sendMarkAsReadAllSlotMessages({ commit }: ActionContext<IState, null>, slot: TNotificationsType) {
      try {
        await this.$apiBFF.sendMarkAsReadAllSlotMessage(slot);
        commit('setMarkAsReadAllSlotMessages', slot);
      } catch (e) {
        this.$logger.error(e);
      }
    },
    async sendMarkAsReadSlotMessage({ commit }: ActionContext<IState, null>, {
      code,
      slot,
    }: {
      code: INotificationMessageBySlot['code'],
      slot: TNotificationsType
    }) {
      try {
        await this.$apiBFF.markNotificationsAsRead(code);
        commit('setMarkAsReadMessage', { code, slot });
      } catch (e) {
        this.$logger.error(e);
      }
    },
    async fetchPromocodes({ commit }: ActionContext<IState, null>) {
      try {
        const promoCodes = await this.$apiBFF.fetchNotificationPromocodes();
        commit('setPromoCodes', promoCodes);
      } catch (e) {
        this.$logger.error(e);
      }
    },
  },

  mutations: {
    setPromoCodes(state: IState, promoCodes: INotificationPromoCode[]) {
      state.promoCodes = promoCodes;
    },
    setMessages(state: IState, { messages, pagination, slot }: {
      messages: INotificationMessageBySlot[],
      pagination: INotificationMessagesBySlotPagination,
      slot: TNotificationsType }) {
      state.notifications[slot].pagination = pagination;
      state.notifications[slot].messages = messages;
    },
    addMessages(state: IState, { messages, pagination, slot }: {
      messages: INotificationMessageBySlot[],
      pagination: INotificationMessagesBySlotPagination,
      slot: TNotificationsType }) {
      const stateMessages = state.notifications[slot].messages;

      state.notifications[slot].messages = [...stateMessages, ...messages];
      state.notifications[slot].pagination = pagination;
    },
    setMarkAsReadAllSlotMessages(state: IState, slot: TNotificationsType) {
      const { messages } = state.notifications[slot] as { messages: INotificationMessageBySlot[] };
      state.notifications[slot].messages = messages.map((message) => {
        if (message?.hasBeenRead === false) {
          // eslint-disable-next-line no-param-reassign
          message.hasBeenRead = true;
        }

        return message;
      });
    },
    setMarkAsReadMessage(state: IState, { slot, code }: { slot: TNotificationsType, code: INotificationMessageBySlot['code'] }) {
      const { messages } = state.notifications[slot] as { messages: INotificationMessageBySlot[] };

      const message = messages.find((i) => i.code === code);

      if (message?.hasBeenRead === false) {
        message.hasBeenRead = true;
      }
    },
  },
};
